<script setup lang="ts">
export type TooltipPosition = 'top' | 'left' | 'bottom' | 'right'
interface TooltipProps {
    title: string
    position?: TooltipPosition
}

const props = withDefaults(defineProps<TooltipProps>(), {
    position: 'top',
})

const VIEWPORT_PADDING = 8

const tooltip = ref<HTMLDetailsElement>()
const summary = ref<HTMLElement>()
const modal = ref<HTMLElement>()
const isOpen = ref(false)
const modalId = useId()

const updatePosition = (): void => {
    if (!summary.value || !modal.value) return

    const trigger = summary.value.getBoundingClientRect()
    const centerX = trigger.left + trigger.width / 2
    const centerY = trigger.top + trigger.height / 2
    const left = { top: centerX, bottom: centerX, left: trigger.left, right: trigger.right }[props.position]
    const top = { top: trigger.top, bottom: trigger.bottom, left: centerY, right: centerY }[props.position]

    modal.value.style.left = `${left}px`
    modal.value.style.top = `${top}px`

    const box = modal.value.getBoundingClientRect()
    const overflowRight = box.right - (window.innerWidth - VIEWPORT_PADDING)
    const overflowLeft = VIEWPORT_PADDING - box.left

    if (overflowRight > 0) {
        modal.value.style.left = `${left - overflowRight}px`
    } else if (overflowLeft > 0) {
        modal.value.style.left = `${left + overflowLeft}px`
    }
}

const toogleTooltip = async (value: boolean): Promise<void> => {
    isOpen.value = value

    if (value) {
        await nextTick()
        updatePosition()
    }
}

const canHover = (): boolean => window.matchMedia('(hover: hover)').matches

const handleClickOutside = (e: Event): void => {
    const target = e.target as Node | null
    // click on the same tooltip
    if (!target || tooltip.value?.contains(target) || modal.value?.contains(target)) return
    // close tooltip
    toogleTooltip(false)
}

const toggleRepositionListeners = (active: boolean): void => {
    if (active) {
        window.addEventListener('scroll', updatePosition, true)
        window.addEventListener('resize', updatePosition)
        return
    }

    window.removeEventListener('scroll', updatePosition, true)
    window.removeEventListener('resize', updatePosition)
}

watch(isOpen, (open) => toggleRepositionListeners(open))

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    toggleRepositionListeners(false)
})
</script>
<template>
  <details
    ref="tooltip"
    class="tooltip d-inline-flex position-relative"
    :open="isOpen"
    @keydown.esc="toogleTooltip(false)"
  >
    <summary
      ref="summary"
      class="tooltip-summary position-relative"
      :class="`tooltip-summary-${props.position}`"
      aria-label="Viac informácií"
      :aria-describedby="isOpen ? modalId : undefined"
      @click.prevent="toogleTooltip(!isOpen)"
      @mouseleave="canHover() && toogleTooltip(false)"
      @mouseenter="canHover() && toogleTooltip(true)"
    >
      <slot />
    </summary>
    <Teleport
      v-if="isOpen"
      to="body"
    >
      <div
        :id="modalId"
        ref="modal"
        role="tooltip"
        class="tooltip-modal"
        :class="`tooltip-modal-${props.position}`"
      >
        {{ props.title }}
      </div>
    </Teleport>
  </details>
</template>
<style>
@import './Tooltip.vars.scss';
</style>
<style scoped lang="scss">
@import './Tooltip.scss';
</style>
